/**
 * Realtime token-usage capture for cost metering.
 *
 * WHY THIS EXISTS: the browser holds the OpenAI realtime websocket directly, so it is
 * the ONLY party that ever sees `response.done.usage` — the authoritative
 * audio-vs-text-vs-cached token split. Realtime is the platform's largest single cost,
 * and the backend cannot observe any of it. Relaying this over the interview websocket
 * is the only way to get true realtime cost.
 *
 * Kept as a pure module (no React, no side effects) because
 * `realtime-interview-context.tsx` is already ~5.4k lines, and because parsing logic
 * that feeds a billing record should be unit-testable on its own.
 *
 * DESIGN NOTES
 *
 * - Batched, but granularity preserved. ~45 responses per interview is negligible WS
 *   chatter; the reason to batch is backend-side (fewer writes, one rate-limit
 *   bucket). The reason NOT to pre-aggregate on the client is that per-response rows
 *   are what make server-side dedup and drill-down possible — and a client-computed
 *   sum is unverifiable, so the backend logs it for reconciliation and never bills it.
 *
 * - No `model` field is sent. The backend derives the billed model from its own
 *   session configuration; a client-supplied name must never be able to select an
 *   arbitrary expensive model.
 *
 * - Every field OpenAI reports is optional, so extraction is defensive throughout and
 *   a missing `usage` is treated as normal rather than an error.
 *
 * - Cached tokens are a SUBSET of input tokens (OpenAI documents this explicitly).
 *   This module relays the raw shape unchanged; the backend does the subtraction once.
 */
/** Flush when this many entries are buffered. */
export const USAGE_BATCH_SIZE = 8;
/** Flush when the oldest buffered entry is this old, even if under batch size. */
export const USAGE_BATCH_MAX_AGE_MS = 15000;
/** Cap unacked batches held for resend, so a dead socket can't grow memory forever. */
export const USAGE_MAX_UNACKED = 20;
const SESSION_STORAGE_PREFIX = 'realtime_usage_pending:';
/** Bound persisted entries so sessionStorage can never be filled. */
const MAX_PERSISTED_ENTRIES = 200;
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const num = (value) => {
    // Reject booleans explicitly: `typeof true === 'boolean'` here, but a looser
    // Number() coercion would happily turn it into 1.
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0)
        return undefined;
    return value;
};
const pickNums = (source, keys) => {
    const out = {};
    if (!isRecord(source))
        return out;
    keys.forEach((key) => {
        const value = num(source[key]);
        if (value !== undefined)
            out[key] = value;
    });
    return out;
};
/**
 * Extract usage from a `response.done` / `response.failed` / `response.incomplete`
 * event. Returns null when the message carries nothing usable.
 *
 * Non-completed statuses are still captured: an interrupted response was billed for
 * the audio it had already produced, and interruptions are frequent here.
 */
export function extractResponseUsage(msg) {
    if (!isRecord(msg))
        return null;
    const response = msg.response;
    if (!isRecord(response))
        return null;
    const id = typeof response.id === 'string' ? response.id : undefined;
    if (!id)
        return null;
    const rawUsage = isRecord(response.usage) ? response.usage : undefined;
    let usage = null;
    if (rawUsage) {
        usage = pickNums(rawUsage, ['total_tokens', 'input_tokens', 'output_tokens']);
        const inDetails = isRecord(rawUsage.input_token_details)
            ? rawUsage.input_token_details
            : undefined;
        if (inDetails) {
            const details = pickNums(inDetails, [
                'text_tokens',
                'audio_tokens',
                'image_tokens',
                'cached_tokens',
            ]);
            const cachedDetails = isRecord(inDetails.cached_tokens_details)
                ? pickNums(inDetails.cached_tokens_details, [
                    'text_tokens',
                    'audio_tokens',
                    'image_tokens',
                ])
                : undefined;
            if (cachedDetails && Object.keys(cachedDetails).length > 0) {
                details.cached_tokens_details = cachedDetails;
            }
            if (Object.keys(details).length > 0)
                usage.input_token_details = details;
        }
        const outDetails = isRecord(rawUsage.output_token_details)
            ? pickNums(rawUsage.output_token_details, ['text_tokens', 'audio_tokens'])
            : undefined;
        if (outDetails && Object.keys(outDetails).length > 0) {
            usage.output_token_details = outDetails;
        }
        if (Object.keys(usage).length === 0)
            usage = null;
    }
    return {
        kind: 'response',
        id,
        status: typeof response.status === 'string' ? response.status : undefined,
        ts: Date.now(),
        usage,
        measured: usage !== null,
    };
}
/**
 * Extract usage from `conversation.item.input_audio_transcription.completed`.
 * Input transcription is a SEPARATE billed meter from the realtime response.
 */
export function extractTranscriptionUsage(msg) {
    if (!isRecord(msg))
        return null;
    const id = typeof msg.item_id === 'string'
        ? msg.item_id
        : typeof msg.id === 'string'
            ? msg.id
            : undefined;
    if (!id)
        return null;
    const rawUsage = isRecord(msg.usage) ? msg.usage : undefined;
    let usage = null;
    if (rawUsage) {
        if (rawUsage.type === 'duration') {
            const seconds = num(rawUsage.seconds);
            usage = seconds !== undefined ? { type: 'duration', seconds } : null;
        }
        else {
            const tokens = pickNums(rawUsage, [
                'input_tokens',
                'output_tokens',
                'total_tokens',
            ]);
            const details = isRecord(rawUsage.input_token_details)
                ? pickNums(rawUsage.input_token_details, ['audio_tokens', 'text_tokens'])
                : undefined;
            if (Object.keys(tokens).length > 0) {
                usage = Object.assign({ type: 'tokens' }, tokens);
                if (details && Object.keys(details).length > 0) {
                    usage.input_token_details = details;
                }
            }
        }
    }
    if (!usage)
        return null;
    return {
        kind: 'transcription',
        id,
        ts: Date.now(),
        usage,
        measured: true,
    };
}
const emptyTotals = () => ({
    responses: 0,
    transcriptions: 0,
    input_tokens: 0,
    output_tokens: 0,
    input_audio_tokens: 0,
    input_text_tokens: 0,
    input_cached_tokens: 0,
    output_audio_tokens: 0,
    output_text_tokens: 0,
    out_audio_seconds: 0,
    in_audio_seconds: 0,
});
/** Buffers entries and emits batches. Never throws. */
export class UsageBuffer {
    constructor() {
        this.pendingEntries = [];
        this.cumulative = emptyTotals();
    }
    add(entry) {
        if (!entry)
            return;
        this.pendingEntries.push(entry);
        this.accumulate(entry);
    }
    get pending() {
        return this.pendingEntries.length;
    }
    get oldestAgeMs() {
        if (this.pendingEntries.length === 0)
            return 0;
        return Date.now() - this.pendingEntries[0].ts;
    }
    /** True when a flush is due on size or age. */
    shouldFlush() {
        if (this.pendingEntries.length === 0)
            return false;
        return (this.pendingEntries.length >= USAGE_BATCH_SIZE ||
            this.oldestAgeMs >= USAGE_BATCH_MAX_AGE_MS);
    }
    /** Drain the buffer into a batch. Returns null when there is nothing to send. */
    takeBatch(opts) {
        const entries = this.pendingEntries;
        // A final frame is still worth sending with no entries: it carries the
        // cumulative totals the backend uses for reconciliation.
        if (entries.length === 0 && !opts.final)
            return null;
        this.pendingEntries = [];
        const batch = {
            type: 'realtime_usage',
            seq: opts.seq,
            session_epoch: opts.sessionEpoch,
            entries,
        };
        if (opts.final) {
            batch.final = true;
            batch.totals = Object.assign({}, this.cumulative);
        }
        return batch;
    }
    totals() {
        return Object.assign({}, this.cumulative);
    }
    /** Re-queue entries from a batch that was never acknowledged. */
    requeue(entries) {
        if (!Array.isArray(entries) || entries.length === 0)
            return;
        // Prepend: older entries should flush first. Totals are NOT re-accumulated —
        // they already counted these when originally added.
        this.pendingEntries = [...entries, ...this.pendingEntries];
    }
    /**
     * Persist pending entries so a hard tab close does not lose them. The next mount
     * drains them; the backend deduplicates by response id, so a replay is safe.
     */
    persist(slug) {
        try {
            if (!slug || this.pendingEntries.length === 0)
                return;
            const payload = JSON.stringify(this.pendingEntries.slice(-MAX_PERSISTED_ENTRIES));
            window.sessionStorage.setItem(SESSION_STORAGE_PREFIX + slug, payload);
        }
        catch (_a) {
            // sessionStorage can be unavailable or full. Losing an estimate is acceptable;
            // breaking the interview is not.
        }
    }
    static restore(slug) {
        try {
            if (!slug)
                return [];
            const key = SESSION_STORAGE_PREFIX + slug;
            const raw = window.sessionStorage.getItem(key);
            window.sessionStorage.removeItem(key);
            if (!raw)
                return [];
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed))
                return [];
            return parsed.filter((entry) => isRecord(entry) &&
                typeof entry.id === 'string' &&
                (entry.kind === 'response' || entry.kind === 'transcription'));
        }
        catch (_a) {
            return [];
        }
    }
    static clearPersisted(slug) {
        try {
            if (slug)
                window.sessionStorage.removeItem(SESSION_STORAGE_PREFIX + slug);
        }
        catch (_a) {
            /* ignore */
        }
    }
    accumulate(entry) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s;
        const totals = this.cumulative;
        if (entry.kind === 'response') {
            totals.responses += 1;
            const usage = ((_a = entry.usage) !== null && _a !== void 0 ? _a : {});
            totals.input_tokens += (_b = usage.input_tokens) !== null && _b !== void 0 ? _b : 0;
            totals.output_tokens += (_c = usage.output_tokens) !== null && _c !== void 0 ? _c : 0;
            totals.input_text_tokens += (_e = (_d = usage.input_token_details) === null || _d === void 0 ? void 0 : _d.text_tokens) !== null && _e !== void 0 ? _e : 0;
            totals.input_audio_tokens += (_g = (_f = usage.input_token_details) === null || _f === void 0 ? void 0 : _f.audio_tokens) !== null && _g !== void 0 ? _g : 0;
            totals.input_cached_tokens += (_j = (_h = usage.input_token_details) === null || _h === void 0 ? void 0 : _h.cached_tokens) !== null && _j !== void 0 ? _j : 0;
            totals.output_text_tokens += (_l = (_k = usage.output_token_details) === null || _k === void 0 ? void 0 : _k.text_tokens) !== null && _l !== void 0 ? _l : 0;
            totals.output_audio_tokens += (_o = (_m = usage.output_token_details) === null || _m === void 0 ? void 0 : _m.audio_tokens) !== null && _o !== void 0 ? _o : 0;
        }
        else {
            totals.transcriptions += 1;
        }
        totals.out_audio_seconds += (_q = (_p = entry.est) === null || _p === void 0 ? void 0 : _p.out_audio_seconds) !== null && _q !== void 0 ? _q : 0;
        totals.in_audio_seconds += (_s = (_r = entry.est) === null || _r === void 0 ? void 0 : _r.in_audio_seconds) !== null && _s !== void 0 ? _s : 0;
    }
}
